'use client';

/**
 * Character Card Component
 *
 * Card design per the reference mock: full-bleed photo inside a violet-edged
 * rounded frame with the character name + purple age.
 * the character (lib/character-card-meta). Entire card links to the
 * character's indexable /catalog profile page (chat CTA lives there).
 * Used on the landing page and character catalog.
 */
import { useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import { MapPin } from 'lucide-react';

import { trackFunnelEvent } from '~/lib/analytics/track';
import { characterVideoUrl } from '~/lib/animated-characters';
import { locationFor, nationalityFor } from '~/lib/character-card-meta';
import { transformedImageUrl } from '~/lib/image-cdn';

import { CardVideo } from './card-video';
import { FavoriteButton } from './favorite-button';

interface CharacterCardProps {
  id: string;
  slug: string;
  name: string;
  age?: number;
  ethnicity?: string | null;
  status?: 'NEW' | 'LIVE' | null;
  image: string;
  description?: string;
  tags?: string[];
  priority?: boolean;
  noHoverScale?: boolean;
  /** Heart in the top-right corner. Off for grids where it makes no sense (own drafts). */
  showFavorite?: boolean;
}

export function CharacterCard({
  id,
  slug,
  name,
  age,
  ethnicity,
  status,
  image,
  priority = false,
  noHoverScale = false,
  showFavorite = true,
}: CharacterCardProps) {
  const videoUrl = characterVideoUrl(slug);
  const nationality = nationalityFor(id, name, ethnicity);
  const location = locationFor(id);
  const [hovered, setHovered] = useState(false);

  return (
    <Link
      // Cards link to the INDEXABLE profile page. They used to point at
      // /chat/<slug>, which is robots-disallowed + noindex,nofollow — every
      // grid page poured its link equity into a black hole and left all 354
      // /catalog/<slug> pages with zero internal links (GSC: 0 clicks, 37
      // impressions in 90 days). The profile page carries the chat CTA.
      href={`/catalog/${slug}`}
      onClick={() => trackFunnelEvent('character_card_clicked', { slug })}
    >
      <div
        className="group animate-fadeIn relative overflow-hidden"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          borderRadius: 26,
          border: hovered
            ? '1.5px solid rgba(168,85,247,0.95)'
            : '1.5px solid rgba(124,102,210,0.45)',
          background: '#0a0812',
          boxShadow: hovered
            ? '0 0 24px rgba(168,85,247,0.5), 0 10px 30px -12px rgba(0,0,0,0.8)'
            : '0 10px 30px -12px rgba(0,0,0,0.8)',
          transition: 'border-color 0.35s ease, box-shadow 0.35s ease',
        }}
      >
        {/* Status Badge */}
        {status && (
          <div className="absolute top-4 left-4 z-10">
            <span
              className={`rounded-full px-3 py-1 text-xs font-bold ${
                status === 'NEW'
                  ? 'bg-red-500 text-white'
                  : 'animate-pulse bg-green-500 text-white'
              }`}
            >
              {status}
            </span>
          </div>
        )}

        {showFavorite && (
          <div className="absolute top-4 right-4 z-10">
            <FavoriteButton characterId={id} variant="overlay" />
          </div>
        )}

        {/* Character Image. Aspect ratio is set inline (not a Tailwind
            aspect-[x/y] class) so the card height never depends on Tailwind
            generating a brand-new arbitrary class. */}
        <div
          className="relative overflow-hidden"
          style={{ aspectRatio: '324 / 520' }}
        >
          {/* Shimmer underlay — sits BEHIND the image and shows through until
              pixels cover it. Never gate the image's own visibility on React
              state: an opacity-0 image is not an LCP candidate, so the old
              `imgLoaded` fade held LCP hostage to hydration (~12s on mobile
              while the file itself finished in ~3s). Keyframes live in
              globals.css (was 20+ duplicate <style> tags per grid). */}
          <span
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(110deg, #16101f 30%, #241a33 50%, #16101f 70%)',
              backgroundSize: '200% 100%',
              animation: 'cardShimmer 1.4s ease-in-out infinite',
            }}
          />
          <Image
            // Supabase render endpoint fits the image in a 1040px box (card is
            // 324x520 CSS, 2x retina height => 1040) — full-capture originals cost 2-3MB/page
            // (PageSpeed 'properly size images'). Vercel's optimizer stays
            // bypassed (unoptimized) for the reasons below.
            src={transformedImageUrl(image, 1040) ?? image}
            alt={`${name}${age ? `, ${age}` : ''}`}
            fill
            className={`object-cover object-top transition-transform duration-500 ${noHoverScale ? '' : 'group-hover:scale-110'}`}
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
            priority={priority}
            fetchPriority={priority ? 'high' : undefined}
            loading={priority ? undefined : 'lazy'}
            // Character media is already AVIF on the Cloudflare CDN (year-long
            // cache). Vercel's optimizer would re-encode AVIF->AVIF — the
            // slowest transcode there is — and cold-transform on first hit,
            // which is what left cards grey. Serve the CDN file directly.
            unoptimized
          />
          {videoUrl && (
            <CardVideo
              src={videoUrl}
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}
          {/* Bottom scrim behind the info block */}
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{
              height: '52%',
              background:
                'linear-gradient(to top, rgba(5,3,10,0.95) 0%, rgba(5,3,10,0.55) 55%, transparent 100%)',
            }}
          />
        </div>

        {/* Info — name + purple age.
            Deliberately NOT a heading: 12+ card names as <h2> pushed every
            grid page's real section headings to positions 13+ in the
            document outline ("Bianca 26" is a link label, not a section). */}
        <div className="absolute right-0 bottom-0 left-0 p-3.5 sm:p-5">
          <p className="truncate text-lg font-extrabold text-white sm:text-2xl">
            {/* Phones show the first name only; full name from sm: up. */}
            {name.split(' ')[0]}{' '}
            {age ? (
              <span
                className="font-extrabold"
                style={{
                  color: hovered ? '#a78bfa' : '#c7c7d1',
                  transition: 'color 0.35s ease',
                }}
              >
                {age}
              </span>
            ) : null}
          </p>

          {/* Location + nationality — desktop only (hidden on phones). */}
          <div className="mt-3 hidden items-center gap-2 sm:flex">
            <MapPin
              className="shrink-0"
              style={{ color: '#a78bfa', width: 17, height: 17 }}
              fill="rgba(167,139,250,0.35)"
            />
            <span className="truncate text-base font-medium text-white">
              {location}
            </span>
          </div>

          <div className="mt-1.5 flex items-center gap-1.5 sm:mt-2 sm:gap-2">
            <span className="shrink-0 text-sm sm:text-base">
              {nationality.flag}
            </span>
            <span className="truncate text-sm font-medium text-white sm:text-base">
              {nationality.label}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
